import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNextUpCourt } from '@/hooks/useNextUpCourt';
import { parseLocalDate } from '@/utils/dateUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trophy, Clock, MapPin, Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Info, Users, Crown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { useTournamentProgress } from '@/hooks/useTournamentProgress';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useTournamentSchedule } from '@/hooks/useTournamentSchedule';
import { useGroupStandings } from '@/hooks/useGroupStandings';
import GroupStandings from '@/components/tournament/GroupStandings';
import EliminationSeedingTable from '@/components/tournament/EliminationSeedingTable';
import { useEliminationSeeding } from '@/hooks/useEliminationSeeding';
import ParticipantsList from '@/components/tournament/ParticipantsList';
import { useSubcategoryParticipants } from '@/hooks/useSubcategoryParticipants';
import { supabase } from '@/integrations/supabase/client';
import EliminationBracketTree from './EliminationBracketTree';

interface TournamentEliminationMatchesViewProps {
  eventId: string;
  player1Name: string | null;
  player2Name: string | null;
  category: string | null;
  subcategory: string | null;
  tournamentTitle?: string;
  sport?: string | null;
}

const TournamentEliminationMatchesView = ({
  eventId,
  player1Name,
  player2Name,
  category,
  subcategory,
  tournamentTitle,
  sport,
}: TournamentEliminationMatchesViewProps) => {
  const isPickleball = (sport || '').toLowerCase().includes('pickle');
  const { language, t } = useLanguage();
  const { nextUpCourt, nextUpCategory, nextUpSubcategory, nextUpPhase } = useNextUpCourt(eventId);

  // Helper to gate banners by category/subcategory + phase from notification metadata
  const sameStr = (a?: string | null, b?: string | null) => {
    const norm = (s?: string | null) => (s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return norm(a) === norm(b);
  };
  const notifMatches = (phaseGroup: 'grupos' | 'eliminacion') => {
    if (!nextUpCourt) return false;
    if (nextUpCategory && category && !sameStr(nextUpCategory, category)) return false;
    if (nextUpSubcategory && subcategory && !sameStr(nextUpSubcategory, subcategory)) return false;
    if (nextUpPhase) {
      const np = nextUpPhase.toLowerCase();
      const isElim = np === 'eliminacion' || np === 'octavos' || np === 'cuartos' || np === 'semifinales' || np === 'final' || np.includes('elimin');
      const isGroup = np === 'grupos' || np.startsWith('grupo');
      if (phaseGroup === 'eliminacion' && !isElim) return false;
      if (phaseGroup === 'grupos' && !isGroup) return false;
    }
    return true;
  };
  const groupNextUp = notifMatches('grupos') ? nextUpCourt : null;
  const elimNextUp = notifMatches('eliminacion') ? nextUpCourt : null;

  const translateGroupName = useCallback((name: string): string => {
    if (language === 'es') return name;
    return name.replace(/^Grupo\b/i, t('tournament.groupPrefix'));
  }, [language, t]);

  const translateCourt = useCallback((court: string): string => {
    if (language === 'es') return court;
    return court.replace(/^Cancha\b/i, t('tournament.court'));
  }, [language, t]);
  const { phase, progress: tournamentProgressValue } = useTournamentProgress(eventId, category, subcategory);

  const translateSpanishDate = useCallback((text: string): string => {
    if (language === 'es') return text.charAt(0).toUpperCase() + text.slice(1);
    const days: Record<string, string> = { lunes: 'Monday', martes: 'Tuesday', miércoles: 'Wednesday', miercoles: 'Wednesday', jueves: 'Thursday', viernes: 'Friday', sábado: 'Saturday', sabado: 'Saturday', domingo: 'Sunday' };
    const months: Record<string, string> = { enero: 'January', febrero: 'February', marzo: 'March', abril: 'April', mayo: 'May', junio: 'June', julio: 'July', agosto: 'August', septiembre: 'September', octubre: 'October', noviembre: 'November', diciembre: 'December' };
    let result = text.toLowerCase().replace(/\bde\b/g, '').replace(/,/g, '').replace(/\s+/g, ' ').trim();
    for (const [es, en] of Object.entries(days)) { result = result.replace(new RegExp(`\\b${es}\\b`, 'i'), en); }
    for (const [es, en] of Object.entries(months)) { result = result.replace(new RegExp(`\\b${es}\\b`, 'i'), en); }
    const match = result.match(/^(\w+)\s+(\d+)\s+(\w+)$/);
    if (match) result = `${match[1]}, ${match[3]} ${match[2]}`;
    return result;
  }, [language]);

  const formatMatchDate = useCallback((dateStr: string) => {
    if (!dateStr) return '';
    const isoMatch = dateStr.match(/^\d{4}-\d{2}-\d{2}/);
    if (!isoMatch) return translateSpanishDate(dateStr);
    const d = parseLocalDate(dateStr);
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    const formatted = d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    const cleaned = language === 'en' ? formatted : formatted.replace(/ de /g, ' ').replace(',', '');
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }, [language, translateSpanishDate]);
  const { eliminationMatches, loading } = useTournamentSchedule(eventId, category, subcategory);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [openPhases, setOpenPhases] = useState<Set<string>>(new Set());
  const [phaseApis, setPhaseApis] = useState<Record<string, CarouselApi>>({});
  const [phaseCurrents, setPhaseCurrents] = useState<Record<string, number>>({});
  const [tournamentRules, setTournamentRules] = useState<any>(null);
  const [eliminationConfig, setEliminationConfig] = useState<any>(null);
  const [userGroupName, setUserGroupName] = useState<string | null>(null);

  useEffect(() => {
    const fetchRules = async () => {
      const { data } = await supabase
        .from('events')
        .select('rules, group_rules')
        .eq('id', eventId)
        .single();
      if (data?.rules) {
        try {
          setTournamentRules(JSON.parse(data.rules));
        } catch (e) {
          console.error('Error parsing rules:', e);
        }
      }
      if (data?.group_rules) {
        try {
          const gr = typeof data.group_rules === 'string' ? JSON.parse(data.group_rules) : data.group_rules;
          if (gr?.elimination_config) {
            setEliminationConfig(gr.elimination_config);
          }
          // Also store adjustments for numGroups
          if (gr?.adjustments) {
            setEliminationConfig((prev: any) => ({ ...prev, adjustments: gr.adjustments }));
          }
        } catch (e) {
          console.error('Error parsing group_rules:', e);
        }
      }
    };
    if (eventId) fetchRules();
  }, [eventId]);

  // Fetch user's group name
  useEffect(() => {
    const fetchUserGroup = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !eventId) return;

      const { data } = await supabase
        .from('registrations')
        .select('group')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
        .not('group', 'is', null)
        .maybeSingle();

      setUserGroupName(data?.group || null);
    };
    fetchUserGroup();
  }, [eventId, category, subcategory]);

  const { standings: groupStandings, loading: standingsLoading } = useGroupStandings(
    eventId,
    userGroupName || '',
    category || '',
    subcategory || undefined
  );

  const { seeding, loading: seedingLoading } = useEliminationSeeding(eventId, category, subcategory);

  const { participants: subcategoryParticipants, loading: participantsLoading, currentUserId: participantsUserId } = useSubcategoryParticipants(eventId, subcategory, category);

  const mapPhaseFormat = (format: string) => {
    const map: Record<string, { es: string; en: string }> = {
      quarters: { es: 'Cuartos de Final', en: 'Quarter Finals' },
      semi: { es: 'Semifinal', en: 'Semifinals' },
      octavos: { es: 'Octavos de Final', en: 'Round of 16' },
      final: { es: 'Final', en: 'Final' },
    };
    return map[format]?.[language] || format;
  };

  const getClassificationText = () => {
    // Priority 1: elimination_config with repechaje
    if (eliminationConfig?.perSubcategory && category && subcategory) {
      const subcatKey = `${category} ${subcategory}`;
      const config = eliminationConfig.perSubcategory[subcatKey];
      if (config?.hasRepechaje) {
        const numGroups = eliminationConfig.adjustments?.[subcatKey]?.numGroups || 1;
        const directPasses = config.directos * numGroups;
        const repechageSpots = config.totalNeeded - directPasses;
        const phaseName = mapPhaseFormat(config.format || 'quarters');
        
        let directText = numGroups > 1
          ? `${directPasses} ${language === 'es' ? 'pase directo' : 'direct pass'}`
          : `${config.directos} ${language === 'es' ? 'pase directo' : 'direct pass'}`;
        
        return `${t('tournament.classify')}: ${directText} ${language === 'es' ? 'a' : 'to'} ${phaseName} + ${repechageSpots} ${language === 'es' ? 'repechaje' : 'playoff'} = ${config.totalNeeded} ${t('tournament.teams')}`;
      }
    }

    // Priority 2: existing logic from rules.genderCategories
    if (!tournamentRules) return null;
    try {
      let categoryRules = null;

      if (category && subcategory) {
        categoryRules = tournamentRules.genderCategories?.[category]?.find(
          (cat: any) => cat.category === subcategory
        );
      }

      if (!categoryRules && tournamentRules.genderCategories) {
        for (const genderKey of Object.keys(tournamentRules.genderCategories)) {
          const cats = tournamentRules.genderCategories[genderKey];
          if (Array.isArray(cats)) {
            const found = cats.find((cat: any) => cat.groupsMin > 0);
            if (found) { categoryRules = found; break; }
          }
        }
      }

      if (!categoryRules) return null;
      const groupsMin = categoryRules.groupsMin || 0;
      const best3rds = categoryRules.best3rds || 0;
      const best4ths = categoryRules.best4ths || 0;
      const hasBreakdown = best3rds > 0 || best4ths > 0;
      const best2nds = hasBreakdown
        ? (categoryRules.best2nds || 0)
        : (categoryRules.wildCardCount || 0);
      const total = groupsMin + best2nds + best3rds + best4ths;

      let parts = [`${groupsMin} ${t('tournament.firstPlaces')}`];
      if (best2nds > 0) parts.push(`${best2nds} ${t('tournament.bestSeconds')}`);
      if (best3rds > 0) parts.push(`${best3rds} ${t('tournament.bestThirds')}`);
      if (best4ths > 0) parts.push(`${best4ths} ${t('tournament.bestFourths')}`);

      return `${t('tournament.classify')}: ${parts.join(' + ')} = ${total} ${t('tournament.teams')}`;
    } catch {
      return null;
    }
  };

  const teamName = player1Name && player2Name
    ? `${player1Name}/${player2Name}`
    : player1Name || '';

  const isGroupPhase = (phase: string | undefined) => {
    if (!phase) return true;
    return phase.toLowerCase().startsWith('grupo');
  };

  const isUserMatch = (match: any) => {
    if (!match.participants || !Array.isArray(match.participants)) return false;
    return match.participants.some((p: string) => {
      if (!teamName) return false;
      return p === teamName || p.includes(player1Name || '___') || (player2Name && p.includes(player2Name));
    });
  };

  const getMatchResultBadge = (match: any) => {
    if (match.status !== 'completed' || match.winnerTeam === undefined) {
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 text-xs">
          {t('tournament.scheduled')}
        </Badge>
      );
    }

    // Determine if user is team1 (participants[0]) or team2 (participants[1])
    const participants = match.participants || [];
    const p1 = participants[0] || '';
    const isUserTeam1 = p1 === teamName || p1.includes(player1Name || '___') || (player2Name && p1.includes(player2Name));

    if (match.winnerTeam === null || match.winnerTeam === 0) {
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-700 bg-yellow-50 text-xs">
          — {language === 'es' ? 'Empate' : 'Draw'}
        </Badge>
      );
    }

    const userWon = (isUserTeam1 && match.winnerTeam === 1) || (!isUserTeam1 && match.winnerTeam === 2);

    if (userWon) {
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50 text-xs">
          ✓ {language === 'es' ? 'Ganaste' : 'You Won'}
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50 text-xs">
        ✗ {language === 'es' ? 'Perdiste' : 'You Lost'}
      </Badge>
    );
  };

  const groupMatches = eliminationMatches.filter((m) => isGroupPhase(m.phase));
  const userGroupMatches = groupMatches.filter(isUserMatch);
  const elimMatches = eliminationMatches.filter((m) => !isGroupPhase(m.phase));

  const matchesByPhase = useMemo(() => {
    const map = new Map<string, typeof elimMatches>();
    elimMatches.forEach(match => {
      const phase = match.phase || t('tournament.elimination');
      if (!map.has(phase)) map.set(phase, []);
      map.get(phase)!.push(match);
    });
    return map;
  }, [elimMatches]);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api, onSelect]);

  const togglePhase = (phase: string) => {
    setOpenPhases(prev => {
      const next = new Set(prev);
      if (next.has(phase)) next.delete(phase);
      else next.add(phase);
      return next;
    });
  };

  const handlePhaseApi = useCallback((phase: string, newApi: CarouselApi) => {
    if (!newApi) return;
    setPhaseApis(prev => ({ ...prev, [phase]: newApi }));
    const onSelectHandler = () => {
      setPhaseCurrents(prev => ({ ...prev, [phase]: newApi.selectedScrollSnap() }));
    };
    onSelectHandler();
    newApi.on('select', onSelectHandler);
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">{ t('league.loadingMatches')}</div>
        </CardContent>
      </Card>
    );
  }

  if (eliminationMatches.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-yellow-600" />
            <CardTitle className="text-base font-bold">{t('tournament.yourMatches')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-6">
            {t('tournament.noMatchesForTournament')}
          </div>
        </CardContent>
      </Card>
    );
  }

  const renderGroupCarousel = () => {
    if (userGroupMatches.length === 0) {
      return (
        <div className="text-center text-muted-foreground py-6">
          {t('tournament.noGroupMatchesForYou')}
        </div>
      );
    }

    return (
      <>
        <div className="flex items-center justify-end mb-2">
          <Badge variant="secondary" className="text-sm font-semibold">
            {current + 1}/{userGroupMatches.length}
          </Badge>
        </div>
        <Carousel setApi={setApi} className="w-full">
          <CarouselContent>
            {(() => {
              const firstPendingIdx = userGroupMatches.findIndex(m => !m.score && !m.winnerTeam);
              const effectiveNextUp = groupNextUp;
              return userGroupMatches.map((match, index) => {
              const opponent = match.participants?.find((p: string) => {
                return p !== teamName && !p.includes(player1Name || '___');
              }) || t('tournament.opponentTBD');
              // El organizador ocultó la cancha preasignada (solo aplica sin asignación efectiva)
              const courtHidden = !match.assignedCourt && (match as any).courtHidden === true;
              const showNextUp = !courtHidden && effectiveNextUp && index === firstPendingIdx;

              return (
                <CarouselItem key={index}>
                  <div className="border rounded-lg overflow-hidden bg-primary/5 border-primary/20">
                    <div className={`py-2 px-3 flex items-center justify-center gap-2 text-sm font-bold border-b ${
                      match.score
                        ? 'bg-gray-100 text-gray-600 border-gray-200'
                        : match.assignedCourt
                          ? 'bg-green-50 text-green-700 border-green-100'
                          : showNextUp
                            ? 'bg-amber-50 text-amber-700 border-amber-100'
                            : 'bg-gray-50 text-gray-400 border-gray-100'
                    }`}>
                      <MapPin className="h-3.5 w-3.5" />
                      <span>
                        {match.score
                          ? (language === 'es' ? 'Finalizado' : 'Finished')
                          : match.assignedCourt
                            ? `${language === 'es' ? 'Asignado en' : 'Assigned to'} ${match.assignedCourt}`
                            : showNextUp
                              ? `${language === 'es' ? 'Próximo en' : 'Next up on'} ${effectiveNextUp}`
                              : courtHidden
                                ? (language === 'es' ? 'Cancha por definir' : 'Court TBD')
                                : (language === 'es' ? 'Sin asignar' : 'Unassigned')}
                      </span>
                    </div>
                    <div className="p-4">
                     <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                        {match.phase ? translateGroupName(match.phase) : t('tournament.groupPrefix')}
                      </Badge>
                      {getMatchResultBadge(match)}
                    </div>
                    <div className="space-y-2">
                      <div className="text-center">
                        <span className="font-medium text-sm text-foreground">{teamName}</span>
                      </div>
                      <div className="text-center text-muted-foreground text-xs font-medium">
                        {match.score ? match.score : 'VS'}
                      </div>
                      <div className="text-center">
                        <span className="font-medium text-sm text-foreground">{opponent}</span>
                      </div>
                      <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground pt-2 border-t border-primary/10">
                        {match.date && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3.5 w-3.5" />
            <span>{formatMatchDate(match.date)}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3.5 w-3.5" />
                          <span>{match.startTime}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>{(() => {
                            // Cancha oculta por el organizador (sin asignación efectiva)
                            if (courtHidden) return language === 'es' ? 'Por definir' : 'TBD';
                            const courtVal = (showNextUp ? effectiveNextUp : '') || match.assignedCourt || '';
                            if (courtVal) {
                              const s = String(courtVal);
                              return s.toLowerCase().startsWith('cancha') ? translateCourt(s) : `${t('tournament.court')} ${s}`;
                            }
                            return typeof match.court === 'number' || !String(match.court).toLowerCase().includes('cancha') ? `${t('tournament.court')} ${match.court}` : translateCourt(String(match.court));
                          })()}</span>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </CarouselItem>
              );
            });
            })()}
          </CarouselContent>
        </Carousel>
        {userGroupMatches.length > 1 && (
          <>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => api?.scrollPrev()} disabled={current === 0}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="flex gap-2">
                {userGroupMatches.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => api?.scrollTo(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === current ? 'bg-primary' : 'bg-muted hover:bg-muted-foreground/30'}`}
                    aria-label={`${t('tournament.goToMatch')} ${idx + 1}`}
                  />
                ))}
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => api?.scrollNext()} disabled={current === userGroupMatches.length - 1}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
            <div className="text-center text-xs text-muted-foreground pt-2">
              {userGroupMatches.length} {t('tournament.scheduledMatches')}
            </div>
          </>
        )}
        {/* Group standings below carousel */}
        {userGroupName && groupStandings.length > 0 && (
          <div className="mt-6">
            <GroupStandings 
              standings={groupStandings} 
              title={t('tournament.groupClassification')} 
              groupName={userGroupName}
              hideSets={true}
              useGamesDiffAsPoints={!isPickleball}
              pointsPerWin={isPickleball ? 3 : undefined}
            />
          </div>
        )}
        {/* Elimination seeding table */}
        <div className="mt-6">
          <EliminationSeedingTable
            seeding={seeding}
            loading={seedingLoading}
            subcategory={subcategory || ''}
            category={category || ''}
            hidePointsAndSets={true}
          />
        </div>
      </>
    );
  };

  const getRoundIcon = (phase: string) => {
    const lower = phase.toLowerCase();
    if (lower.includes('final') && !lower.includes('semi') && !lower.includes('cuarto') && !lower.includes('octavo')) return '🏆';
    if (lower.includes('semi')) return '🥈';
    if (lower.includes('cuarto')) return '🥉';
    return '🎯';
  };

  const getRoundBadgeColor = (phase: string) => {
    const lower = phase.toLowerCase();
    if (lower.includes('final') && !lower.includes('semi') && !lower.includes('cuarto')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (lower.includes('semi')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (lower.includes('cuarto')) return 'bg-purple-100 text-purple-800 border-purple-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  const renderElimMatches = () => {
    if (elimMatches.length === 0) {
      return (
        <div className="text-center text-muted-foreground py-6">
          {t('tournament.noElimMatchesYet')}
        </div>
      );
    }

    const classificationText = getClassificationText();

    return (
      <div className="space-y-3">
        {classificationText && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Trophy className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span className="text-sm font-medium text-primary">{classificationText}</span>
          </div>
        )}

        <EliminationBracketTree
          eliminationMatches={elimMatches}
          player1Name={player1Name}
          player2Name={player2Name}
          nextUpCourt={elimNextUp}
          nextUpPhase={elimNextUp ? nextUpPhase : null}
        />
      </div>
    );
  };

  return (
    <Card>
      <CardContent className="pt-6">
        {tournamentTitle && (
          <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] text-white">
            <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
              <Trophy className="h-5 w-5" />
              {tournamentTitle}
            </h3>
            <div className="relative">
              <Progress value={tournamentProgressValue} className="h-2 bg-white/20 [&>div]:bg-white" />
              <div className="flex justify-between mt-3">
                {[
                  { label: t('tournament.groups'), icon: Users, key: 'groups' },
                  { label: t('tournament.elimination'), icon: Trophy, key: 'elimination' },
                  { label: t('tournament.final'), icon: Crown, key: 'final' },
                ].map((step, idx) => {
                  const isActive = phase === step.key || phase === 'finished';
                  const isPast = (phase === 'elimination' && step.key === 'groups') ||
                    (phase === 'final' && (step.key === 'groups' || step.key === 'elimination')) ||
                    phase === 'finished';
                  return (
                    <div key={step.key} className={`flex flex-col items-center gap-1 ${isActive || isPast ? 'opacity-100' : 'opacity-50'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isActive ? 'bg-white text-[#31633f]' : isPast ? 'bg-white/80 text-[#31633f]' : 'bg-white/20 text-white'}`}>
                        <step.icon className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-xs font-medium">{step.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <Tabs defaultValue="mi-grupo" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 h-auto p-1 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10">
            <TabsTrigger value="mi-grupo" className="flex flex-col items-center gap-1 py-2 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="h-4 w-4" />
              {t('tournament.myGroup')}
            </TabsTrigger>
            <TabsTrigger value="participantes" className="flex flex-col items-center gap-1 py-2 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="h-4 w-4" />
              {t('tournament.participants')}
            </TabsTrigger>
            <TabsTrigger value="eliminacion" className="flex flex-col items-center gap-1 py-2 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Trophy className="h-4 w-4" />
              {t('tournament.elimination')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="mi-grupo">
            {renderGroupCarousel()}
          </TabsContent>
          <TabsContent value="participantes">
            <ParticipantsList
              participants={subcategoryParticipants}
              currentUserId={participantsUserId}
              loading={participantsLoading}
              subcategory={subcategory || ''}
              category={category || ''}
            />
          </TabsContent>
          <TabsContent value="eliminacion">
            {renderElimMatches()}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TournamentEliminationMatchesView;
